import {
  CATEGORIES,
  CoreError,
  CURRENCY_INFO,
  calculateSplit,
  convertMinor,
  formatMoney,
  formatScaled,
  fromMinor,
  parseScaled,
  toMinor,
  type Category,
  type CurrencyCode,
  type SplitMethod,
  type SplitSpec,
} from '@expenditure/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useToast } from '../components/Toast';
import { Avatar, CurrencySelect, ErrorState, Field, PageHeader, PageSkeleton, Segmented } from '../components/ui';
import { ApiError, api } from '../lib/api';
import { amountPlaceholder, convertAmountInput, errorMessage, isValidAmountInput, rateNote, todayIso } from '../lib/format';
import { resizeForUpload } from '../lib/image';
import { useExpense, useGroup, useInvalidateGroup, usePlan } from '../lib/queries';
import { haptic } from '../lib/telegram';
import type { Expense, ExpenseInput, Frequency, GroupDetail, Member, Plan, ReceiptDraft, SplitInput } from '../lib/types';
import { Icon } from '../components/Icon';
import { UpgradeSheet } from '../components/Pro';
import { ReceiptCamera, cameraSupported } from '../components/ReceiptCamera';
import { prepareReceiptFile } from '../lib/receipt-photo';

export function ExpenseFormPage() {
  const { groupId = '', expenseId } = useParams();
  const { t } = useTranslation();
  const group = useGroup(groupId);
  const expense = useExpense(groupId, expenseId);

  if (group.isPending || (expenseId && expense.isPending)) return <PageSkeleton />;
  const error = group.error ?? expense.error;
  if (error || !group.data) return <ErrorState message={errorMessage(error, t)} />;
  return <ExpenseForm detail={group.data} existing={expense.data} />;
}

type SplitValues = Record<string, string>;

/** Pre-fills percentages that sum to exactly 100 (e.g. 33.34 / 33.33 / 33.33). */
function equalPercents(memberIds: string[]): SplitValues {
  if (memberIds.length === 0) return {};
  const parts = calculateSplit(10000, { method: 'equal', participants: memberIds.map((memberId) => ({ memberId })) });
  return Object.fromEntries(parts.map((p) => [p.memberId, formatScaled(p.amountMinor, 2).replace(/\.00$/, '')]));
}

function ExpenseForm({ detail, existing }: { detail: GroupDetail; existing?: Expense }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateGroup();
  const { group, me, members } = detail;

  const [description, setDescription] = useState(existing?.description ?? '');
  const [currency, setCurrency] = useState<CurrencyCode>(existing?.originalCurrency ?? group.currency);
  const [amount, setAmount] = useState(existing ? fromMinor(existing.originalAmountMinor, existing.originalCurrency) : '');
  const [paidBy, setPaidBy] = useState(existing?.paidByMemberId ?? me.id);
  const [category, setCategory] = useState<Category>(existing?.category ?? 'food');
  const [date, setDate] = useState(existing?.expenseDate ?? todayIso());
  const [note, setNote] = useState(existing?.note ?? '');
  // New expenses only: add it again every week or month.
  const [repeat, setRepeat] = useState<Frequency | 'never'>('never');
  const [method, setMethod] = useState<SplitMethod>(existing?.splitMethod ?? 'equal');
  const [selected, setSelected] = useState<string[]>(
    existing ? existing.splits.map((s) => s.memberId) : members.map((m) => m.id),
  );
  const [values, setValues] = useState<Record<SplitMethod, SplitValues>>(() => {
    const fromExisting = Object.fromEntries(existing?.splits.map((s) => [s.memberId, s.inputValue ?? '']) ?? []);
    return {
      equal: {},
      exact: existing?.splitMethod === 'exact' ? fromExisting : {},
      percentage: existing?.splitMethod === 'percentage' ? fromExisting : equalPercents(members.map((m) => m.id)),
      shares:
        existing?.splitMethod === 'shares' ? fromExisting : Object.fromEntries(members.map((m) => [m.id, '1'])),
    };
  });
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<ReceiptDraft | null>(null);

  // Keep the default percentages at 100% when people are toggled in or out.
  useEffect(() => {
    if (!existing) setValues((v) => ({ ...v, percentage: equalPercents(selected) }));
  }, [selected, existing]);

  const participants = members.filter((m) => selected.includes(m.id));
  const amountValid = isValidAmountInput(amount, currency);
  const totalMinor = amountValid ? toMinor(amount, currency) : 0;

  /** Live preview using the exact same engine as the server. */
  const preview = useMemo(() => {
    if (!amountValid || participants.length === 0) return { shares: new Map<string, number>(), error: null };
    try {
      const spec = buildSpec(method, participants, values[method], currency);
      const shares = calculateSplit(totalMinor, spec);
      return { shares: new Map(shares.map((s) => [s.memberId, s.amountMinor])), error: null };
    } catch (error) {
      return { shares: new Map<string, number>(), error: error instanceof CoreError ? error.code : 'INVALID_SPLIT_VALUE' };
    }
  }, [amountValid, participants, method, values, currency, totalMinor]);

  const status = splitStatus(method, participants, values[method], currency, totalMinor, t);
  const converted =
    amountValid && currency !== group.currency
      ? formatMoney(convertMinor(totalMinor, currency, group.currency, group.rates), group.currency)
      : null;

  const formValid = description.trim().length > 0 && amountValid && participants.length > 0 && !preview.error;

  const toggle = (memberId: string) => {
    haptic.select();
    setSelected((s) => (s.includes(memberId) ? s.filter((id) => id !== memberId) : [...s, memberId]));
  };
  const setValue = (memberId: string, value: string) =>
    setValues((v) => ({ ...v, [method]: { ...v[method], [memberId]: value.replace(',', '.').replace(/[^\d.]/g, '') } }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (!formValid || submitting) {
      haptic.error();
      return;
    }
    setSubmitting(true);
    const input: ExpenseInput = {
      description: description.trim(),
      amount: amount.trim(),
      currency,
      paidByMemberId: paidBy,
      category,
      expenseDate: date,
      note: note.trim() || null,
      split: toSplitInput(method, participants, values[method]),
      ...(receipt && !existing ? { source: 'receipt' as const } : {}),
      ...(!existing && repeat !== 'never' ? { repeat } : {}),
    };
    try {
      const saved = existing
        ? await api.updateExpense(group.id, existing.id, input)
        : await api.createExpense(group.id, input);
      await invalidate(group.id);
      toast(!existing && repeat !== 'never' ? t(`expenseForm.repeatSaved.${repeat}`) : t('common.saved'));
      void navigate(existing ? `/groups/${group.id}/expenses/${saved.expense.id}` : `/groups/${group.id}`, {
        replace: true,
      });
    } catch (error) {
      toast(errorMessage(error, t), 'error');
      setSubmitting(false);
    }
  };

  return (
    <form className="page page--no-tabbar app" onSubmit={(e) => void submit(e)} noValidate>
      <PageHeader title={existing ? t('expenseForm.titleEdit') : t('expenseForm.titleNew')} />

      {!existing && (
        <ReceiptScanner
          groupId={group.id}
          onDraft={(draft) => {
            setReceipt(draft);
            if (draft.merchant) setDescription(draft.merchant);
            if (draft.amount) {
              setCurrency(draft.currency);
              setAmount(draft.amount);
            }
            if (draft.date) setDate(draft.date);
            setCategory(draft.category);
          }}
        />
      )}
      {receipt && receipt.items.length > 0 && (
        <details className="card small">
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            {t('receipt.items', { count: receipt.items.length })}
          </summary>
          <div className="list" style={{ marginTop: 8 }}>
            {receipt.items.map((item, i) => (
              <div className="row" key={i} style={{ justifyContent: 'space-between', padding: '4px 0' }}>
                <span className="ellipsis">{item.name}</span>
                <span className="num muted">{item.amount ?? '—'}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="stack" style={{ gap: 8 }}>
        <div className="amount-input">
          <span className="num" style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-2)' }}>
            {CURRENCY_INFO[currency].symbol}
          </span>
          <input
            className="num"
            inputMode="decimal"
            placeholder={amountPlaceholder(currency)}
            aria-label={t('expenseForm.amount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))}
          />
          <CurrencySelect
            compact
            value={currency}
            label={t('expenseForm.currency')}
            onChange={(next) => {
              // Switching currency converts what was typed (55500 ៛ → 13.88 $).
              setAmount((typed) => convertAmountInput(typed, currency, next, group.rates));
              setCurrency(next);
            }}
          />
        </div>
        {touched && !amountValid && <span className="field-error">{t('errors.invalidAmount')}</span>}
        {converted && (
          <span className="field-hint">
            {t('expenseForm.converted', { amount: converted, rate: rateNote([currency, group.currency], group.rates) })}
          </span>
        )}
      </div>

      <Field
        label={t('expenseForm.description')}
        error={touched && !description.trim() ? t('errors.required') : undefined}
      >
        <input
          className={`input ${touched && !description.trim() ? 'input--invalid' : ''}`}
          value={description}
          maxLength={120}
          placeholder={t('expenseForm.descriptionPlaceholder')}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      <div className="field">
        <span className="field-label">{t('expenseForm.category')}</span>
        <div className="chips">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              aria-pressed={category === c}
              onClick={() => (haptic.select(), setCategory(c))}
            >
              <Icon name={c} size={16} />
              {t(`categories.${c}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <Field label={t('expenseForm.paidBy')}>
          <select className="input" value={paidBy} onChange={(e) => setPaidBy(e.target.value)}>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === me.id ? `${m.displayName} (${t('common.you')})` : m.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('expenseForm.date')}>
          <input className="input" type="date" value={date} max="9999-12-31" onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>

      {!existing && (
        <div className="field">
          <span className="field-label">{t('expenseForm.repeat')}</span>
          <Segmented<Frequency | 'never'>
            value={repeat}
            onChange={(r) => (haptic.select(), setRepeat(r))}
            options={[
              { value: 'never', label: t('expenseForm.repeatNever') },
              { value: 'weekly', label: t('expenseForm.repeatWeekly') },
              { value: 'monthly', label: t('expenseForm.repeatMonthly') },
            ]}
          />
          {repeat !== 'never' && <span className="field-hint">{t(`expenseForm.repeatHint.${repeat}`)}</span>}
        </div>
      )}

      <div className="field">
        <span className="field-label">{t('expenseForm.split')}</span>
        <Segmented<SplitMethod>
          value={method}
          onChange={(m) => (haptic.select(), setMethod(m))}
          options={(['equal', 'exact', 'percentage', 'shares'] as const).map((m) => ({
            value: m,
            label: t(`splitMethods.${m}`),
          }))}
        />
      </div>

      <div className="card card--flush">
        <div className="section-title" style={{ margin: 0, padding: '12px 16px 4px' }}>
          <span>{t('expenseForm.participants')}</span>
          <button
            type="button"
            onClick={() => setSelected(selected.length === members.length ? [] : members.map((m) => m.id))}
          >
            {t('expenseForm.selectAll')}
          </button>
        </div>
        <div className="list">
          {members.map((m) => (
            <ParticipantRow
              key={m.id}
              member={m}
              isMe={m.id === me.id}
              checked={selected.includes(m.id)}
              onToggle={() => toggle(m.id)}
              method={method}
              value={values[method][m.id] ?? ''}
              onValue={(v) => setValue(m.id, v)}
              share={preview.shares.get(m.id)}
              currency={currency}
            />
          ))}
        </div>
        {status && (
          <div className={`split-status ${status.ok ? 'muted' : 'neg'}`}>
            <span>{status.left}</span>
            <span className="num">{status.right}</span>
          </div>
        )}
      </div>
      {touched && preview.error && <div className="alert">{t(`errors.${preview.error}`)}</div>}
      {touched && participants.length === 0 && <div className="alert">{t('errors.NO_PARTICIPANTS')}</div>}

      <Field label={`${t('expenseForm.note')} (${t('common.optional')})`}>
        <textarea
          className="input"
          value={note}
          maxLength={500}
          placeholder={t('expenseForm.notePlaceholder')}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>

      <div className="sticky-footer">
        <button className="btn btn--primary btn--block" disabled={submitting}>
          {t('expenseForm.save')}
        </button>
      </div>
    </form>
  );
}

function ParticipantRow(props: {
  member: Member;
  isMe: boolean;
  checked: boolean;
  onToggle: () => void;
  method: SplitMethod;
  value: string;
  onValue: (value: string) => void;
  share: number | undefined;
  currency: CurrencyCode;
}) {
  const { t } = useTranslation();
  const { member, checked, method } = props;
  return (
    <div className="list-row" style={{ cursor: 'default' }}>
      <button
        type="button"
        className="row grow"
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left' }}
        onClick={props.onToggle}
        aria-pressed={checked}
      >
        <span className={`checkbox ${checked ? 'checkbox--on' : ''}`} aria-hidden>
          {checked && <Icon name="check" size={14} />}
        </span>
        <Avatar name={member.displayName} photo={member.profilePhoto} small />
        <span className="grow">
          <span className="list-title ellipsis" style={{ display: 'block' }}>
            {member.displayName}
            {props.isMe && <span className="muted"> ({t('common.you')})</span>}
          </span>
          {checked && props.share !== undefined && (
            <span className="list-sub num">{formatMoney(props.share, props.currency)}</span>
          )}
        </span>
      </button>
      {checked && method !== 'equal' && (
        <div className="row" style={{ gap: 6 }}>
          {method === 'exact' && <span className="muted num">{CURRENCY_INFO[props.currency].symbol}</span>}
          <input
            className="split-input num"
            inputMode="decimal"
            placeholder="0"
            value={props.value}
            aria-label={member.displayName}
            onChange={(e) => props.onValue(e.target.value)}
          />
          {method === 'percentage' && <span className="muted">%</span>}
          {method === 'shares' && <span className="muted">×</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildSpec(method: SplitMethod, participants: Member[], values: SplitValues, currency: CurrencyCode): SplitSpec {
  const v = (m: Member) => values[m.id]?.trim() || '0';
  switch (method) {
    case 'equal':
      return { method, participants: participants.map((m) => ({ memberId: m.id })) };
    case 'exact':
      return { method, participants: participants.map((m) => ({ memberId: m.id, amountMinor: toMinor(v(m), currency) })) };
    case 'percentage':
      return { method, participants: participants.map((m) => ({ memberId: m.id, percent: v(m) })) };
    case 'shares':
      return { method, participants: participants.map((m) => ({ memberId: m.id, shares: v(m) })) };
  }
}

function toSplitInput(method: SplitMethod, participants: Member[], values: SplitValues): SplitInput {
  const v = (m: Member) => values[m.id]?.trim() || '0';
  switch (method) {
    case 'equal':
      return { method, participants: participants.map((m) => ({ memberId: m.id })) };
    case 'exact':
      return { method, participants: participants.map((m) => ({ memberId: m.id, amount: v(m) })) };
    case 'percentage':
      return { method, participants: participants.map((m) => ({ memberId: m.id, percent: v(m) })) };
    case 'shares':
      return { method, participants: participants.map((m) => ({ memberId: m.id, shares: v(m) })) };
  }
}

/** Running total under the participant list: "$5.00 left", "10% too much", "6 shares". */
function splitStatus(
  method: SplitMethod,
  participants: Member[],
  values: SplitValues,
  currency: CurrencyCode,
  totalMinor: number,
  t: (key: string, vars?: Record<string, unknown>) => string,
): { left: string; right: string; ok: boolean } | null {
  const sum = (scale: number) =>
    participants.reduce((acc, m) => {
      try {
        return acc + parseScaled(values[m.id]?.trim() || '0', scale);
      } catch {
        return acc;
      }
    }, 0n);

  if (method === 'exact') {
    const assigned = Number(sum(CURRENCY_INFO[currency].minorDigits));
    const diff = totalMinor - assigned;
    return {
      left: `${formatMoney(assigned, currency)} / ${formatMoney(totalMinor, currency)}`,
      right:
        diff >= 0
          ? t('expenseForm.left', { amount: formatMoney(diff, currency) })
          : t('expenseForm.over', { amount: formatMoney(-diff, currency) }),
      ok: diff === 0,
    };
  }
  if (method === 'percentage') {
    const diff = 10000n - sum(2);
    const shown = formatScaled(diff < 0n ? -diff : diff, 2).replace(/\.00$/, '');
    return {
      left: `${formatScaled(sum(2), 2).replace(/\.00$/, '')}% / 100%`,
      right: diff >= 0n ? t('expenseForm.percentLeft', { value: shown }) : t('expenseForm.percentOver', { value: shown }),
      ok: diff === 0n,
    };
  }
  if (method === 'shares') {
    return { left: t('splitMethods.shares'), right: formatScaled(sum(2), 2).replace(/\.00$/, ''), ok: true };
  }
  return null;
}

/** "Scan receipt": photo → AI draft that fills the form. Hidden if the server has no AI. */
function ReceiptScanner({ groupId, onDraft }: { groupId: string; onDraft: (draft: ReceiptDraft) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const closeCamera = useCallback(() => setCameraOpen(false), []);
  const cameraUnavailable = useCallback(() => {
    // No live camera (e.g. permission denied): use the phone's own camera/photo picker.
    setCameraOpen(false);
    input.current?.click();
  }, []);
  const queryClient = useQueryClient();
  const features = useQuery({ queryKey: ['features'], queryFn: api.features, staleTime: Infinity });
  const plan = usePlan();
  if (!features.data?.receiptScan) return null;

  const left = plan.data ? Math.max(0, plan.data.scansLimit - plan.data.scansUsed) : null;
  const isFree = plan.data?.tier === 'free';

  /** A photo from the picker: find, straighten and clean the receipt first. */
  const scanFile = async (file: File | undefined) => {
    if (!file) return;
    setScanning(true);
    const photo = await prepareReceiptFile(file).catch(() => resizeForUpload(file));
    await scan(photo);
  };

  const scan = async (photo: Blob) => {
    setScanning(true);
    try {
      const { draft, plan: updated } = await api.scanReceipt(groupId, photo);
      // Merge: keep what only GET /me/plan knows (how to pay for Pro).
      queryClient.setQueryData<Plan>(['plan'], (old) => (old ? { ...old, ...updated } : updated));
      onDraft(draft);
      haptic.success();
      const doubtful = draft.amountEstimated || draft.checkTotal;
      toast(
        !draft.amount
          ? t('receipt.noTotal')
          : draft.amountEstimated
            ? t('receipt.estimatedTotal')
            : draft.checkTotal
              ? t('receipt.checkTotal')
              : t('receipt.check'),
        draft.amount && !doubtful ? 'success' : 'error',
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'SCAN_LIMIT_REACHED') {
        void queryClient.invalidateQueries({ queryKey: ['plan'] });
        setUpgradeOpen(true);
      } else {
        toast(errorMessage(error, t), 'error');
      }
    } finally {
      setScanning(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void scanFile(e.target.files?.[0])}
      />
      <button
        type="button"
        className="btn btn--soft btn--block"
        disabled={scanning}
        onClick={() =>
          isFree && left === 0 ? setUpgradeOpen(true) : cameraSupported() ? setCameraOpen(true) : input.current?.click()
        }
      >
        <Icon name={scanning ? 'loader' : 'camera'} size={18} className={scanning ? 'spin' : undefined} />
        {scanning ? t('receipt.reading') : t('receipt.scan')}
        {isFree && left !== null && !scanning && <span className="scan-count num">{t('pro.leftShort', { count: left })}</span>}
      </button>
      <UpgradeSheet open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={t('pro.limitReason')} />
      {cameraOpen && (
        <ReceiptCamera
          onCapture={(photo) => {
            setCameraOpen(false);
            void scan(photo);
          }}
          onClose={closeCamera}
          onPickPhoto={() => {
            setCameraOpen(false);
            input.current?.click();
          }}
          onUnavailable={cameraUnavailable}
        />
      )}
    </>
  );
}
